import type { NostrEvent, NPool } from '@nostrify/nostrify';
import { INDEX_RELAYS, FALLBACK_RELAYS } from './blogConfig';

/** A parsed NIP-65 relay entry. */
export interface RelayEntry {
  url: string;
  read: boolean;
  write: boolean;
}

/** A user's resolved relay list. */
export interface RelayList {
  /** Relays the user writes to (where their events are published). */
  write: string[];
  /** Relays the user reads from (where mentions/replies to them are sent). */
  read: string[];
  /** The raw kind 10002 event, if found. */
  event?: NostrEvent;
}

/** Normalize a relay URL: ensure ws(s):// scheme and a trailing slash. */
export function normalizeRelayUrl(url: string): string {
  let u = url.trim();
  if (!u) return u;
  if (!/^wss?:\/\//i.test(u)) {
    u = `wss://${u}`;
  }
  // Ensure a single trailing slash on the origin-only URLs.
  try {
    const parsed = new URL(u);
    // Keep pathname as-is but normalize trailing slash for root
    if (parsed.pathname === '') parsed.pathname = '/';
    return parsed.toString();
  } catch {
    return u.endsWith('/') ? u : `${u}/`;
  }
}

/** Parse a kind 10002 event into read/write relay lists. */
export function parseRelayList(event: NostrEvent): RelayList {
  const write: string[] = [];
  const read: string[] = [];

  for (const tag of event.tags) {
    if (tag[0] !== 'r' || !tag[1]) continue;
    const url = normalizeRelayUrl(tag[1]);
    const marker = tag[2];
    if (!marker) {
      write.push(url);
      read.push(url);
    } else if (marker === 'write') {
      write.push(url);
    } else if (marker === 'read') {
      read.push(url);
    }
  }

  return {
    write: [...new Set(write)],
    read: [...new Set(read)],
    event,
  };
}

/**
 * In-memory cache of resolved relay lists per pubkey, so we don't repeatedly
 * hit the index relays during a session.
 */
const relayListCache = new Map<string, RelayList>();

/**
 * Discover a user's NIP-65 relay list following the outbox model.
 *
 * Strategy: query the well-known public index relays first for the user's
 * kind 10002. Pick the newest one found. Falls back to a set of popular relays
 * if nothing is discoverable.
 */
export async function discoverRelayList(
  pool: NPool,
  pubkey: string,
  opts: { signal?: AbortSignal } = {},
): Promise<RelayList> {
  const cached = relayListCache.get(pubkey);
  if (cached) return cached;

  let newest: NostrEvent | undefined;

  try {
    const group = pool.group(INDEX_RELAYS);
    const events = await group.query(
      [{ kinds: [10002], authors: [pubkey], limit: 1 }],
      { signal: opts.signal ?? AbortSignal.timeout(4000) },
    );
    for (const ev of events) {
      if (!newest || ev.created_at > newest.created_at) newest = ev;
    }
  } catch {
    // ignore — fall through to fallback below
  }

  // If not found on index relays, try the fallback relays as a last resort.
  if (!newest) {
    try {
      const group = pool.group(FALLBACK_RELAYS);
      const events = await group.query(
        [{ kinds: [10002], authors: [pubkey], limit: 1 }],
        { signal: opts.signal ?? AbortSignal.timeout(4000) },
      );
      for (const ev of events) {
        if (!newest || ev.created_at > newest.created_at) newest = ev;
      }
    } catch {
      // ignore
    }
  }

  let result: RelayList;
  if (newest) {
    result = parseRelayList(newest);
    // Guard against empty lists.
    if (result.write.length === 0) result.write = [...FALLBACK_RELAYS];
    if (result.read.length === 0) result.read = [...FALLBACK_RELAYS];
  } else {
    result = { write: [...FALLBACK_RELAYS], read: [...FALLBACK_RELAYS] };
  }

  relayListCache.set(pubkey, result);
  return result;
}

/** Clear the cached relay list for a pubkey (e.g. after an update). */
export function invalidateRelayList(pubkey: string): void {
  relayListCache.delete(pubkey);
}

/**
 * Given a set of tagged pubkeys, resolve the union of their read relays.
 * Used when publishing an event that mentions users (so it reaches their inbox).
 */
export async function resolveReadRelaysFor(
  pool: NPool,
  pubkeys: string[],
  opts: { signal?: AbortSignal } = {},
): Promise<string[]> {
  const lists = await Promise.all(
    pubkeys.map((pk) => discoverRelayList(pool, pk, opts).catch(() => null)),
  );
  const relays = new Set<string>();
  for (const list of lists) {
    if (!list) continue;
    for (const url of list.read) relays.add(url);
  }
  return [...relays];
}
